import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DASHBOARD_TOUR_STEPS } from "@/components/OnboardingTour";

/**
 * useDashboardData — single source of truth for all Dashboard API calls.
 *
 * Splits fetches into two waves:
 *   Primary (blocks the loading state): summary, cashflow, breakdown,
 *     insights, streak — everything the north-star grid needs.
 *   Secondary (fires in parallel, does not block the render): nudge,
 *     tour plan, circle-of-week, sprout moments, portfolio update.
 *
 * All secondary fetches swallow errors — the cards they power render
 * nothing on failure, so a flaky API can't blank the whole dashboard.
 */
export default function useDashboardData() {
  const [summary, setSummary] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [insights, setInsights] = useState(null);
  const [streak, setStreak] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [nudgeRefreshing, setNudgeRefreshing] = useState(false);
  const [tourSteps, setTourSteps] = useState(null);
  const [circleWeek, setCircleWeek] = useState(null);
  const [moments, setMoments] = useState(null);
  const [portfolioUpdate, setPortfolioUpdate] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPrimary = useCallback(async () => {
    try {
      const [s, cf, br, ins, st] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/reports/cashflow"),
        api.get("/reports/expense-breakdown"),
        api.get("/optimizer/insights"),
        api.get("/streak"),
      ]);
      setSummary(s.data);
      setCashflow(cf.data);
      setBreakdown(br.data);
      setInsights(ins.data);
      setStreak(st.data);
    } finally { setLoading(false); }
  }, []);

  const loadNudge = useCallback(async () => {
    try {
      const { data } = await api.get("/insights/nudge");
      setNudge(data);
      try {
        const dismissedDate = localStorage.getItem("pfo_nudge_dismissed_date");
        if (dismissedDate === data.date) setNudgeDismissed(true);
      } catch (e) { console.debug("nudge dismissed-date read failed:", e); }
    } catch (e) { console.debug("daily nudge fetch failed:", e); }
  }, []);

  const loadTour = useCallback(async () => {
    try {
      const done = localStorage.getItem("pfo_tour_completed");
      if (done) { setTourSteps([]); return; }
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/onboarding/tour-plan");
      setTourSteps(Array.isArray(data?.steps) ? data.steps : DASHBOARD_TOUR_STEPS);
    } catch {
      setTourSteps(DASHBOARD_TOUR_STEPS);
    }
  }, []);

  const loadCircleWeek = useCallback(async () => {
    try {
      const { data } = await api.get("/circles/week");
      setCircleWeek(data);
    } catch { /* silent — card just won't render */ }
  }, []);

  const loadMoments = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/recent-moments");
      setMoments(data?.moments || []);
    } catch { setMoments([]); }
  }, []);

  const loadPortfolioUpdate = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/portfolio-update");
      setPortfolioUpdate(data?.update || null);
    } catch { setPortfolioUpdate(null); }
  }, []);

  const dismissNudge = useCallback(() => {
    setNudgeDismissed(true);
    try {
      if (nudge) localStorage.setItem("pfo_nudge_dismissed_date", nudge.date);
    } catch { /* ignore */ }
  }, [nudge]);

  const refreshNudge = useCallback(async () => {
    setNudgeRefreshing(true);
    try {
      const { data } = await api.get("/insights/nudge?refresh=true");
      setNudge(data);
      setNudgeDismissed(false);
      try { localStorage.removeItem("pfo_nudge_dismissed_date"); } catch { /* ignore */ }
    } catch { toast.error("Could not refresh nudge"); }
    finally { setNudgeRefreshing(false); }
  }, []);

  useEffect(() => {
    loadPrimary();
    loadNudge();
    loadTour();
    loadCircleWeek();
    loadMoments();
    loadPortfolioUpdate();
  }, [loadPrimary, loadNudge, loadTour, loadCircleWeek, loadMoments, loadPortfolioUpdate]);

  return {
    summary, cashflow, breakdown, insights, streak,
    nudge, nudgeDismissed, nudgeRefreshing,
    tourSteps, circleWeek, moments, portfolioUpdate,
    loading,
    dismissNudge, refreshNudge,
  };
}
